import React, { useRef } from "react";

import Image from "next/image";
import Link from "next/link";

import { EditOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Column, ORDERING, Record } from "@gsmvalve/gsmvalve-table";
import { t, Trans } from "@lingui/macro";
import { Button, Row, Tag, Typography } from "antd";
import { keyBy } from "lodash";
import { BareFetcher } from "swr";

import AppLayout from "layout/app";

import EmptyStates from "components/empty-states";
import FuelCard from "components/fuel-card";
import { ActionMenu } from "components/navigations/header";
import Table, { onHasFilters, pageCountMessage, TableSearch } from "components/table";

import { useApplicationContext } from "contexts/app";

import { DATA_TYPE, FILTER_CONDITION, IFilter } from "@gsmvalve/gsmvalve-table/dist/cjs/types";
import { FuelCard as FuelCardType } from "types";

const limit = 100;

const filters = (basePath: string | undefined, account: string, client: BareFetcher<any>): IFilter[] => [
  {
    key: "driver",
    title: t`Driver`,
    type: DATA_TYPE.CHOICE,
    condition: FILTER_CONDITION.IN,
    autoComplete: {
      url: `${basePath}drivers/?account=${account}&active=true`,
      label: "Search",
      mode: "multiple",
      valueKey: "id",
      labelKey: "name",
      client: client,
    },
  },
  {
    key: "vehicle",
    title: t`Vehicle`,
    type: DATA_TYPE.CHOICE,
    condition: FILTER_CONDITION.IN,
    autoComplete: {
      url: `${basePath}vehicles/?account=${account}&active=true`,
      label: "Search",
      mode: "multiple",
      valueKey: "id",
      labelKey: "name_display",
      client: client,
    },
  },
  { key: "account", omit: true, title: "" },
  { key: "created_at", omit: true, title: "" },
  { key: "user", omit: true, title: "" },
  { key: "url", omit: true, title: "" },
  { key: "active", omit: true, title: "" },
  { key: "fuel_station_chain", omit: true, title: "" },
  { key: "id", title: t`ID`, supported: [FILTER_CONDITION.EQUALS] },
];

const modifier = (data: any) => {
  const records: Record[] = [];

  data?.forEach((rows: any) => {
    const row = rows?.data;
    const accounts = keyBy(row?.accounts, "url");
    const vehicles = keyBy(row?.vehicles, "url");
    const drivers = keyBy(row?.drivers, "url");
    const chains = keyBy(row?.fuel_station_chains, "url");

    row?.fuel_cards?.forEach((card: any) => {
      records.push({
        ...card,
        account: accounts[card.account]?.display_name,
        vehicle: vehicles[card.vehicle]?.name_display,
        driver: drivers[card.driver]?.name,
        fuel_station_chain: chains[card.fuel_station_chain]?.name_display,
      });
    });
  });

  return records;
};

const newFuelCardButton = (
  <Link href={"/settings/fuel-cards/new/"}>
    <Button type={"primary"} icon={<PlusCircleOutlined />}>
      <>
        <Trans>New fuel card</Trans>
      </>
    </Button>
  </Link>
);

const FuelCards = () => {
  const { session, httpClient } = useApplicationContext();
  const mutateTable = useRef<any>(null);

  const url = `fuel_cards/?account=${session?.accountId}&sideload=vehicles,drivers,fuel_station_chains&count=true&active=true`;
  const columns: Column[] = [
    {
      key: "number",
      title: t`Number`,
      isVisible: true,
      dataIndex: ["number"],
      fixed: true,
      type: DATA_TYPE.CUSTOM,
      render: (data) => {
        return <Link href={`/settings/fuel-cards/${data?.id}`}>{data?.number}</Link>;
      },
    },
    {
      key: "fuel_station_chain",
      title: t`Retail chain`,
      isVisible: true,
      render: (data) => {
        return <Typography.Text>{data?.fuel_station_chain}</Typography.Text>;
      },
    },
    {
      key: "vehicle",
      title: t`Vehicle`,
      isVisible: true,
      dataIndex: ["vehicle"],
    },
    {
      key: "driver",
      title: t`Driver`,
      isVisible: true,
      dataIndex: ["driver"],
    },
    {
      key: "pin",
      title: t`PIN`,
      isVisible: true,
      dataIndex: ["pin"],
    },
    {
      key: "expiration_date",
      title: t`Card expiration date`,
      isVisible: true,
      width: 250,
      dataIndex: ["expiration_date"],
    },
    { key: "card_expiration_date", title: t`Card expiration date` },
    { key: "pin", title: t`PIN` },
    {
      key: "active",
      width: 100,
      title: t`Status`,
      isVisible: true,
      dataIndex: ["active"],
      type: DATA_TYPE.CUSTOM,
      render: (data) => {
        if (data.active === true) {
          return (
            <Tag color={"green"}>
              <Trans>Active</Trans>
            </Tag>
          );
        }

        return (
          <Tag>
            <Trans>Inactive</Trans>
          </Tag>
        );
      },
    },
    {
      key: "actions",
      title: t`Actions`,
      width: 90,
      render: (data) => {
        return (
          <Link href={`/settings/fuel-cards/${data?.id}/`}>
            <Button type={"text"} icon={<EditOutlined />} />
          </Link>
        );
      },
      isVisible: false,
    },
    { key: "account", omit: true },
    { key: "user", omit: true },
    { key: "created_at", omit: true },
    { key: "url", omit: true },
    { key: "name_display", omit: true },
    { key: "updated_at", omit: true },
  ];

  return (
    <AppLayout pageTitle={t`Fuel cards`} fullscreen={true}>
      <ActionMenu
        menus={[
          <Row>
            <TableSearch placeholder={t`Search fuel cards...`} />
          </Row>,
          <Row>{newFuelCardButton}</Row>,
        ]}
      />
      <Table<FuelCardType[]>
        key={"fuel_cards"}
        id={`${session?.accountId}-fuel-cards`}
        columns={{
          filtersPath: "/fuel_cards/",
          path: "FuelCard",
          customColumns: columns,
          customFilters: filters(httpClient.basePath, String(session?.accountId), httpClient.swrFetcher),
        }}
        mutateTable={mutateTable}
        records={{
          url: url,
          sort: [{ key: "name", order: "asc" as ORDERING }],
          limit: limit,
          modifier: modifier,
        }}
        tabsFilter={{
          key: "active",
          default: "true",
          filters: [
            { value: "true", label: t`Active` },
            { value: "false", label: t`Inactive` },
            { value: "", label: t`All` },
          ],
        }}
        controls={{
          tabSort: true,
          filterBtn: true,
          manageColumn: true,
          gridLayout: true,
          tableLayout: true,
          pageCountMessage: pageCountMessage(t`Fuel cards`),
        }}
        renderItemWidth={300}
        gridRender={(data) => {
          return <FuelCard data={data} />;
        }}
        empty={{
          emptyWithoutFilter: (
            <EmptyStates
              title={t`No fuel cards yet`}
              description={t`Add fuel cards using the button below.`}
              icon={
                <Image
                  src={"/images/fuel-cards-empty-state.svg"}
                  width={260}
                  // @ts-ignore
                  height={260}
                  alt={t`fuel cards empty state`}
                />
              }
              actions={[newFuelCardButton]}
            />
          ),
          onHasFilter: onHasFilters(),
        }}
      />
    </AppLayout>
  );
};

export default FuelCards;
